import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../Global/Modals/Modal';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import { withApollo } from 'react-apollo';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import TextFieldInput from '../../Global/Inputs/TextField';
import CheckboxInput from '../../Global/Inputs/Checkbox';
import OutlinedSelectInput from '../../Global/Inputs/OutlinedSelect';
import DateFieldInput from '../../Global/Inputs/DateField';
import Autocomplete from '../../Global/Inputs/Autocomplete';

import combineStyles from '../../../utils/combineStyles';
import TypographyStyles from '../../../styles/typography';
import LayoutStyles from '../../../styles/layout';
import SpacingStyles from '../../../styles/helpers/spacing';
import ButtonStyles from '../../../styles/button';
import Grid from '@material-ui/core/Grid';
import GridItem from './../../Global/GridItem';
import { AppContext } from '../../../AppProvider';

import CustomRateModel from '../../../Models/CustomRate';
import CustomRateSchema from './CustomRate.schema';

import { Formik, Field } from 'formik';

const uuidv1 = require('uuid/v1');

const styles = theme => ({
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    fontSize: '20px',
    fontWeight: '500'
  },

  actionButtons: {
    padding: 0,
    width: 96
  },
  titleWrapper: {
    marginBottom: theme.spacing.unit * 4.125
  },
  titleContainer: {
    alignItems: 'center',
    borderBottom: '1px solid rgba(12,31,44,0.12)',
    display: 'flex',
    height: 56,
    padding: [[0, theme.spacing.unit * 2, 0, theme.spacing.unit * 2]],
    position: 'relative'
  },
  volumeCheckbox: { float: 'right', marginTop: '-19px' }
});

export class AddCustomRate extends Component {
  constructor(props) {
    super(props);

    // if caller passed in a boolean for volume use it, otherwise default to
    // whatever is on the rate.
    const isVolumeRate = props.volume
      ? true
      : props.rate
      ? props.rate.isVolumeRate
      : false;

    this.state = {
      rate: props.rate
        ? { ...props.rate, isVolumeRate }
        : { ...CustomRateModel(), isVolumeRate },
      availableCarriers: props.availableCarriers
    };
  }

  static propTypes = {
    volume: PropTypes.bool,
    rate: PropTypes.object
  };

  _handleChange = ({ target: { name, value } }) => {
    this.setState({ [name]: value });
  };

  _handleOnClose = () => {
    const { onClose, rate } = this.props;
    this.setState(
      {
        ...rate
      },
      onClose
    );
  };

  _handleOnSave = values => {
    const { onSave } = this.props;
    const rate = Number(String(values.rate).replace(/,/g, ''));

    onSave({
      ...values,
      rate: rate,
      carrierGrandTotal: rate,
      customRateItemGuid: uuidv1()
    });
  };

  _getSuggestions = async value => {
    const availableCarriers = this.state.availableCarriers.map(
      availableCarrier => ({
        label: availableCarrier.name,
        value: availableCarrier.id
      })
    );

    const suggestions = availableCarriers.filter(availableCarrier =>
      RegExp(value).test(availableCarrier.label)
    );

    return Promise.resolve(suggestions);
  };

  render() {
    const { t, classes } = this.props;
    const { rate } = this.state;
    const { serviceLevels } = this.context.globalProps.referenceData;

    return (
      <Formik
        validationSchema={CustomRateSchema({ t })}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{ ...rate }}
        onSubmit={async values => {
          this._handleOnSave(values);
        }}
        onReset={(values, actions) => {
          actions.resetForm();
          this._handleOnClose();
        }}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          dirty
        }) => (
          <Modal
            open={true}
            onClose={this._handleOnClose}
            classes={{
              root: classes.modalRoot,
              body: classes.modalBody,
              footer: classes.modalFooter
            }}
            body={
              <Fragment>
                <div className={classes.titleContainer}>
                  <span
                    className={classnames(classes.headline8, classes.title)}
                  >
                    {t('Add Custom Rate')}
                    <span
                      className={classnames(
                        classes.body2Light,
                        classes.volumeCheckbox
                      )}
                    >
                      <CheckboxInput
                        label={t('Volume Rate')}
                        name="isVolumeRate"
                        value={values.isVolumeRate}
                        onChange={handleChange}
                      />
                    </span>
                  </span>
                </div>
                <Grid container spacing={16}>
                  <GridItem lg={6}>
                    <Field
                      name="carrier"
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <Autocomplete
                          getSuggestions={this._getSuggestions}
                          onSuggestionSelected={carrier => {
                            handleChange({
                              target: {
                                name: 'carrier',
                                value: carrier.label
                              }
                            });

                            handleChange({
                              target: {
                                name: 'carrierId',
                                value: carrier.value
                              }
                            });
                          }}
                          label={t('Carrier')}
                          value={values.carrier}
                          onChange={change => {
                            handleChange({
                              target: {
                                name: 'carrier',
                                value: change
                              }
                            });

                            handleChange({
                              target: {
                                name: 'carrierId',
                                value: null
                              }
                            });
                          }}
                          field={field}
                          form={form}
                          classes={{
                            root: classes.noMarginBottom
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      value={values.quoteId}
                      name="quoteId"
                      onChange={handleChange}
                      label={t('Quote ID')}
                      component={TextFieldInput}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      value={values.level}
                      name={'level'}
                      onChange={handleChange}
                      label={t('Level')}
                      component={OutlinedSelectInput}
                      options={serviceLevels}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      value={values.transit_time}
                      name={'transit_time'}
                      onChange={handleChange}
                      label={t('Transit Time')}
                      component={TextFieldInput}
                      endAdornment={'days'}
                      mask={'yearDays'}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      value={values.estimated_delivery}
                      name={'estimated_delivery'}
                      onChange={handleChange}
                      label={t('Estimated Delivery Date')}
                      component={DateFieldInput}
                      format={'MMM DD, YYYY'}
                      savingFormat={'YYYY-MM-DD'}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      value={values.rate}
                      name={'rate'}
                      onChange={handleChange}
                      label={t('Rate')}
                      component={TextFieldInput}
                      startAdornment={'$'}
                      mask={'currency'}
                    />
                  </GridItem>
                  <GridItem lg={12}>
                    <Field
                      value={values.notes}
                      name={'notes'}
                      onChange={handleChange}
                      label={t('Notes')}
                      component={TextFieldInput}
                      multiline={true}
                      classes={{
                        root: classes.noMarginBottom
                      }}
                    />
                  </GridItem>
                </Grid>
              </Fragment>
            }
            footer={
              <Fragment>
                <Button
                  color={'primary'}
                  className={classnames(classes.button, classes.actionButtons)}
                  classes={{
                    label: classes.buttonLabel
                  }}
                  onClick={handleReset}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  color={'primary'}
                  className={classnames(classes.button, classes.actionButtons)}
                  classes={{
                    label: classes.buttonLabel,
                    disabled: classes.buttonDisabled
                  }}
                  disabled={!dirty}
                  onClick={handleSubmit}
                >
                  {values.id ? t('Update') : t('Save')}
                </Button>
              </Fragment>
            }
          />
        )}
      />
    );
  }
}

AddCustomRate.contextType = AppContext;
const AddCustomRateWithTranslations = withNamespaces()(
  withApollo(AddCustomRate)
);
export default withStyles(
  combineStyles(
    TypographyStyles,
    LayoutStyles,
    SpacingStyles,
    ButtonStyles,
    styles
  )
)(AddCustomRateWithTranslations);
